import { Avatar, Box } from "@mui/material";
import { useMemo } from "react";
import { GroupIcon } from "../../../../../components/icons";
import Button from "../../../../../components/ui/Button";
import Text from "../../../../../components/ui/Text";
import {
  GAME_INVITATION_FREQUENCY,
  GAME_PAYMENT_MODE,
  GAME_VISIBILITY,
} from "../../../../../constants/game";
import { formatGameDateTime } from "../../../../../utils/formatters";
import { formatTeamSize } from "../../../../../utils/game";

const INVITATION_FREQUENCY_LABELS = {
  [GAME_INVITATION_FREQUENCY.NO_INVITE]: "No email invite",
  [GAME_INVITATION_FREQUENCY["1_DAYS_BEFORE"]]: "1 day before the game",
  [GAME_INVITATION_FREQUENCY["2_DAYS_BEFORE"]]: "2 days before the game",
  [GAME_INVITATION_FREQUENCY["3_DAYS_BEFORE"]]: "3 days before the game",
  [GAME_INVITATION_FREQUENCY["4_DAYS_BEFORE"]]: "4 days before the game",
};

const ReviewSection = ({ title, stepNumber, onEditStep, children }) => (
  <Box
    sx={{
      backgroundColor: "white",
      borderRadius: "8px",
      border: "1px solid #E5E5E5",
      p: 2,
      mb: 2,
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1,
      }}
    >
      <Text variant="paragraph" sx={{ fontWeight: 600 }}>
        {title}
      </Text>
      <Button
        variant="tertiary"
        onClick={() => onEditStep(stepNumber)}
        sx={{ width: "auto", minWidth: "auto", p: 0, color: "#1890FF" }}
      >
        Edit
      </Button>
    </Box>
    {children}
  </Box>
);

const ReviewStep = ({ gameData, group, isNewGroup, onEditStep }) => {
  const groupImageUrl = useMemo(() => {
    if (isNewGroup && group?.image) {
      return URL.createObjectURL(group.image);
    }
    return group?.avatar_image_url;
  }, [isNewGroup, group]);

  const paymentSummary =
    gameData.payment_mode === GAME_PAYMENT_MODE.IN_APP
      ? `€${gameData.totalAmount} per player (in app)`
      : gameData.use_revolut
        ? `€${gameData.playerAmount} per player, via Revolut`
        : `€${gameData.playerAmount} per player, collected yourself`;

  return (
    <Box>
      <Text variant="subtitle3" sx={{ mb: 2 }}>
        Review your game
      </Text>

      {gameData.venue && (
        <Text variant="pageHeader" sx={{ mb: 2, fontSize: "24px" }}>
          {gameData.venue}
        </Text>
      )}

      <ReviewSection
        title="Sport and visibility"
        stepNumber={1}
        onEditStep={onEditStep}
      >
        <Text variant="paragraph3" sx={{ color: "#666" }}>
          {gameData.sport} -{" "}
          {gameData.visibility === GAME_VISIBILITY.PRIVATE
            ? "Private"
            : "Public"}
        </Text>
      </ReviewSection>

      <ReviewSection
        title="Date, time and repeat"
        stepNumber={2}
        onEditStep={onEditStep}
      >
        <Text variant="paragraph3" sx={{ color: "#666" }}>
          {gameData.date && gameData.time
            ? formatGameDateTime(`${gameData.date}T${gameData.time}`)
            : "Not set"}
          {gameData.recurring ? " - repeats weekly" : ""}
        </Text>
        {gameData.recurring && (
          <Text variant="paragraph3" sx={{ color: "#666" }}>
            Group invite email:{" "}
            {INVITATION_FREQUENCY_LABELS[gameData.invitation_frequency] ||
              "No email invite"}
          </Text>
        )}
      </ReviewSection>

      <ReviewSection title="Location" stepNumber={3} onEditStep={onEditStep}>
        <Text variant="paragraph3" sx={{ color: "#666" }}>
          {gameData.venue || "Not set"}
        </Text>
        <Text variant="paragraph3" sx={{ color: "#666" }}>
          {gameData.location?.address}
        </Text>
      </ReviewSection>

      <ReviewSection
        title="Player details"
        stepNumber={4}
        onEditStep={onEditStep}
      >
        <Text variant="paragraph3" sx={{ color: "#666" }}>
          {formatTeamSize(gameData.players)}
          {gameData.whatsapp_link ? " - WhatsApp link added" : ""}
        </Text>
      </ReviewSection>

      <ReviewSection
        title="Price and payment"
        stepNumber={5}
        onEditStep={onEditStep}
      >
        <Text variant="paragraph3" sx={{ color: "#666" }}>
          {paymentSummary}
        </Text>
      </ReviewSection>

      <ReviewSection title="Group" stepNumber={6} onEditStep={onEditStep}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar src={groupImageUrl} sx={{ width: 36, height: 36 }}>
            <GroupIcon width={24} height={24} />
          </Avatar>
          <Text variant="paragraph3" sx={{ color: "#666" }}>
            {group?.name || "Not set"}
            {isNewGroup ? " (new group)" : ""}
          </Text>
        </Box>
      </ReviewSection>

      <Text variant="paragraph3" sx={{ color: "#A1A1A1", mt: 1 }}>
        This is what players will see once you publish.
      </Text>
    </Box>
  );
};

export default ReviewStep;
